import React, { useState, useEffect } from 'react';
import { Terminal } from '../../types';
import { useEnhancedApi } from '../../hooks/useEnhancedApi';
import { useToast } from '../ui/Toast';
import { permissionChecks } from '../../lib/auth/helpers';

export const TerminalsManagement: React.FC = () => {
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState<Terminal | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    terminal_name: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const { showToast } = useToast();
  
  // Use authenticated API service
  const enhancedApi = useEnhancedApi();

  useEffect(() => {
    loadTerminals();
  }, []);

  const loadTerminals = async () => {
    try {
      setLoading(true);
      const allTerminals = await enhancedApi.getTerminals();
      setTerminals(allTerminals);
    } catch (error) {
      console.error('Failed to load terminals:', error);
      showToast('Failed to load terminals', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    
    // Simple validation - can be enhanced later
    if (!formData.terminal_name.trim()) {
      errors.terminal_name = 'Terminal name is required';
    } else if (formData.terminal_name.length > 100) {
      errors.terminal_name = 'Terminal name must be 100 characters or less';
    }
    
    if (formData.description && formData.description.length > 500) {
      errors.description = 'Description must be 500 characters or less';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      if (editingTerminal) {
        await enhancedApi.updateTerminal(editingTerminal.terminal_id, {
          terminal_name: formData.terminal_name,
          description: formData.description.trim() || undefined
        });
        showToast('Terminal updated successfully!', 'success');
      } else {
        await enhancedApi.createTerminal({
          terminal_name: formData.terminal_name,
          description: formData.description.trim() || undefined
        });
        showToast('Terminal created successfully!', 'success');
      }
      
      resetForm();
      await loadTerminals();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save terminal';
      setFormErrors({ submit: errorMessage });
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (terminal: Terminal) => {
    setEditingTerminal(terminal);
    setFormData({
      terminal_name: terminal.terminal_name,
      description: terminal.description || ''
    });
    setShowAddForm(true);
    setFormErrors({});
  };

  const handleDelete = async (terminal: Terminal) => {
    if (!window.confirm(`Are you sure you want to delete "${terminal.terminal_name}"?`)) {
      return;
    }
    
    setLoading(true);
    try {
      await enhancedApi.deleteTerminal(terminal.terminal_id);
      showToast('Terminal deleted successfully!', 'success');
      await loadTerminals();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete terminal';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      terminal_name: '',
      description: ''
    });
    setFormErrors({});
    setShowAddForm(false);
    setEditingTerminal(null);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Terminals Management</h1>
          <p className="text-gray-400 text-sm">Add, edit, and manage terminals</p>
        </div>
        {permissionChecks.canUpdateTerminals() && (
          <button 
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors text-sm font-medium"
          >
            {showAddForm ? 'Cancel' : 'Add Terminal'}
          </button>
        )}
      </div>
      
      {/* Add/Edit Terminal Form */}
      {showAddForm && (
        <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingTerminal ? 'Edit Terminal' : 'Add New Terminal'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Terminal Name Field */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Terminal Name <span className="text-red-400">*</span>
              </label>
              <input 
                type="text" 
                name="terminal_name"
                value={formData.terminal_name}
                onChange={handleInputChange}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.terminal_name ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter terminal name (e.g., Terminal A, Terminal 1)" 
                disabled={loading}
              />
              {formErrors.terminal_name && (
                <p className="mt-1 text-sm text-red-400">{formErrors.terminal_name}</p>
              )}
            </div>

            {/* Description Field */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea 
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                  formErrors.description ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter terminal description (optional)" 
                disabled={loading}
              />
              {formErrors.description && (
                <p className="mt-1 text-sm text-red-400">{formErrors.description}</p>
              )}
              <p className="mt-1 text-xs text-gray-400">Maximum 500 characters</p>
            </div>

            {/* Submit Error */}
            {formErrors.submit && (
              <div className="p-3 bg-red-900/50 border border-red-500 rounded-md">
                <p className="text-sm text-red-400">{formErrors.submit}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button 
                type="submit" 
                disabled={loading}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white rounded transition-colors font-medium"
              >
                {loading ? (editingTerminal ? 'Updating...' : 'Creating...') : (editingTerminal ? 'Update Terminal' : 'Create Terminal')}
              </button>
              <button 
                type="button" 
                onClick={resetForm}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Terminal List */}
      <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
        <h3 className="text-lg font-semibold mb-4">Terminal List</h3>
        {loading && !showAddForm ? (
          <div className="text-center py-8">
            <div className="loading-spinner mx-auto mb-2"></div>
            <p className="text-gray-400">Loading terminals...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {(Array.isArray(terminals) ? terminals : []).filter(terminal => terminal.is_active).map((terminal) => (
              <div key={terminal.terminal_id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 bg-gray-700 rounded space-y-2 sm:space-y-0">
                <div className="flex-1">
                  <div className="font-medium text-white">{terminal.terminal_name}</div>
                  {terminal.description && (
                    <div className="text-sm text-gray-400 mt-1">{terminal.description}</div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    Created: {new Date(terminal.created_date).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  {permissionChecks.canUpdateTerminals() && (
                    <button 
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs transition-colors"
                      onClick={() => handleEdit(terminal)}
                      disabled={loading}
                    >
                      Edit
                    </button>
                  )}
                </div>
              </div>
            ))}
            {(Array.isArray(terminals) ? terminals : []).filter(terminal => terminal.is_active).length === 0 && !loading && (
              <div className="text-center py-8 text-gray-400">
                No terminals found. Create your first terminal to get started.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
